import { NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { BoondockFacade } from '@curbnturf/boondock-listing/src/lib/+state/boondock.facade';
import { BoondockPhotosComponent } from '@curbnturf/boondock-listing/src/lib/boondock-photos/boondock-photos.component';
import { IBoondock, ILandType, IPhoto } from '@curbnturf/entities';
import {
  CheckedComponent,
  DisplayValidationErrorsDirective,
  IPhotoForm,
  photoFormBuilder,
} from '@curbnturf/form-helpers';
import { boondockComplete } from '@curbnturf/helpers';
import { HazardInputComponent } from '@curbnturf/host-forms/src/lib/hazard-input/hazard-input.component';
import { ListingTerrainComponent } from '@curbnturf/host-forms/src/lib/listing-terrain/listing-terrain.component';
import * as ListingActions from '@curbnturf/listing/src/lib/+state/listing.actions';
import { IonicModule } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-boondock-description',
  templateUrl: './boondock-description.component.html',
  styleUrls: ['./boondock-description.component.scss'],
  standalone: true,
  imports: [
    BoondockPhotosComponent,
    CheckedComponent,
    DisplayValidationErrorsDirective,
    FormsModule,
    IonicModule,
    HazardInputComponent,
    ListingTerrainComponent,
    NgIf,
    ReactiveFormsModule,
  ],
})
export class BoondockDescriptionComponent implements OnInit, OnChanges {
  @Input() boondock: IBoondock;

  subscriptions: Subscription[] = [];

  hazardForm: FormGroup<{
    potentialHazards: FormControl<boolean | null>;
    potentialHazardDescription: FormControl<string | null>;
  }>;

  photoForm: FormGroup<{
    photos: FormArray<FormGroup<IPhotoForm>>;
  }>;

  descriptionForm: FormGroup<{
    name: FormControl<string | null>;
    description: FormControl<string | null>;
  }>;

  landTypesForm: FormArray<FormControl<ILandType['type'] | null>>;

  boondockComplete = boondockComplete;

  get description() {
    if (this.hazardForm) {
      return this.descriptionForm.controls.description;
    }

    return;
  }

  get descriptionLength(): number {
    if (this.description) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((this.description as any)._pendingValue) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (this.description as any)._pendingValue.length;
      }

      return this.description.value?.length || 0;
    }

    return 0;
  }

  get landTypes(): FormArray<FormControl<ILandType['type'] | null>> {
    if (this.hazardForm) {
      return this.landTypesForm;
    }

    return this.fb.array<FormControl<ILandType['type'] | null>>([]);
  }

  get landTypesFormValue(): ILandType['type'][] {
    if (this.hazardForm) {
      return this.landTypesForm.controls.map((el) => {
        return el.value as ILandType['type'];
      });
    }
    return [];
  }

  get name() {
    if (this.hazardForm) {
      return this.descriptionForm.controls.name;
    }

    return;
  }

  constructor(
    private fb: FormBuilder,
    private boondockFacade: BoondockFacade,
    private alertFacade: AlertFacade,
    private actions$: Actions,
  ) {}

  ngOnInit() {
    this.makeForms();

    this.subscriptions.push(
      this.actions$.pipe(ofType(ListingActions.listingImagesUpdated)).subscribe((action) => {
        this.updatePhotos(action.photos);
      }),
    );
  }

  ngOnChanges() {
    this.makeForms();
  }

  updatePhotos(photos: IPhoto[]) {
    this.photoForm.controls.photos.clear();
    photos?.forEach((photo) => {
      this.photoForm.controls.photos.push(photoFormBuilder(this.fb, photo));
    });
  }

  makeForms() {
    this.photoForm = this.fb.group({
      photos: this.fb.array<FormGroup<IPhotoForm>>([]),
    });

    this.hazardForm = this.fb.group({
      potentialHazards: this.fb.control(false),
      potentialHazardDescription: this.fb.control(''),
    });

    this.descriptionForm = this.fb.group({
      name: this.fb.control<string | null>('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(32),
      ]),
      description: this.fb.control<string | null>('', [Validators.required, Validators.minLength(150)]),
    });

    this.landTypesForm = this.fb.array<FormControl<ILandType['type'] | null>>([]);

    if (this.boondock) {
      this.hazardForm.patchValue({
        potentialHazards: this.boondock.potentialHazards,
        potentialHazardDescription: this.boondock.potentialHazardDescription,
      });

      this.descriptionForm.patchValue({
        description: this.boondock.description,
        name: this.boondock.name,
      });

      this.updatePhotos(this.boondock.photos ?? []);

      this.boondock.landTypes?.forEach((land) => {
        this.landTypesForm.push(this.fb.control(land));
      });
    }

    this.subscriptions.push(
      this.descriptionForm.valueChanges
        .pipe(
          tap(() => {
            this.descriptionForm.markAsTouched();
            this.descriptionForm.markAsDirty();
            this.name?.markAsTouched();
            this.name?.markAsDirty();
            this.description?.markAsTouched();
            this.description?.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveDescription()),
      this.landTypesForm.valueChanges
        .pipe(
          tap(() => {
            this.landTypesForm.markAsTouched();
            this.landTypesForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveLandTypes()),
      this.hazardForm.valueChanges
        .pipe(
          tap(() => {
            this.hazardForm.markAsTouched();
            this.hazardForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveHazard()),
    );
  }

  saveDescription() {
    if (this.descriptionForm.valid && this.boondock.id) {
      this.boondock = {
        ...this.boondock,
        name: this.name?.value as string,
        description: this.description?.value === null ? undefined : this.description?.value,
      };

      this.boondockFacade.update(this.boondock);
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  saveLandTypes() {
    if (this.landTypesForm.valid && this.boondock.id) {
      this.boondock = {
        ...this.boondock,
        landTypes: this.landTypes?.value?.filter((type) => type) as ILandType['type'][],
      };
      this.boondockFacade.update(this.boondock);
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  saveHazard() {
    if (this.hazardForm.valid && this.boondock.id) {
      this.boondock = {
        ...this.boondock,
        potentialHazards: this.hazardForm.controls.potentialHazards?.value || false,
        potentialHazardDescription: this.hazardForm.controls.potentialHazardDescription?.value || '',
      };
      this.boondockFacade.update(this.boondock);
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }
}
